/**
 * "Have we already asked this person to sign in again, and did it not help?"
 *
 * Sending a user to the login form is a plausible remedy for ONE 401. Doing it
 * a second time, having already watched it fail, is a loop — and a loop is
 * strictly worse than the misleading-but-static screen it replaced, because a
 * static screen can at least be read.
 *
 * It is not hypothetical. The 401 that prompted this work fires IMMEDIATELY
 * AFTER A SUCCESSFUL LOGIN: the login POST returns 200 and caches a user, and
 * every authenticated request after it — the workspace fetch, `/api/users/me`,
 * the socket handshake — comes back 401. An unlatched "on 401, sign in again"
 * turns that into login → 401 → login → 401, forever.
 *
 * The latch is per PAGE LOAD, deliberately: it lives in module state, so it
 * survives the SPA navigation to /login and back (which is the whole loop) and
 * is dropped by a real reload (which is a person deciding to start over). It is
 * also reset by any successful fetch, so a session that lapses later in a long
 * session still gets its one honest bounce.
 *
 * Import-free, for the reason in `apiFailure.ts`.
 */

let attempted = false;

/**
 * True while the sign-in-again remedy is still worth trying. Read it before
 * clearing the cached session — the two go together, because clearing without
 * redirecting strands the user and redirecting without clearing bounces
 * straight back off the login form's already-signed-in guard.
 */
export function shouldAttemptReauth(): boolean {
    return !attempted;
}

/** Spend the one attempt. */
export function markReauthAttempted(): void {
    attempted = true;
}

/**
 * Give the attempt back. Call on any successful authenticated fetch: the
 * session demonstrably works now, so a later expiry is a new problem and
 * deserves its own bounce.
 */
export function resetReauthAttempts(): void {
    attempted = false;
}
